import React, { useState } from 'react';
import { Table, Button, Tag, Space, Tooltip, InputNumber, Form, Drawer, Card, Statistic, Row, Col, message, Spin, DatePicker, Select, Modal, Input, Divider } from 'antd';
import {
    DollarOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
    EyeOutlined,
    CheckOutlined,
    FileTextOutlined,
    UserOutlined,
    CalendarOutlined,
    FilterOutlined,
    ClearOutlined,
    PrinterOutlined,
    CheckCircleFilled,
    CloseOutlined,
    TagOutlined,
    GiftOutlined,
    CloseCircleFilled,
} from '@ant-design/icons';
import { FaFileInvoiceDollar, FaMoneyBillWave, FaTrash } from 'react-icons/fa';
import dayjs from 'dayjs';

import { useUserAccount, useInvoiceMutations, useInvoice } from '../../hooks/useInvoice';
import { getImg } from '../../Api/getImg';
import drEasyApi from '../../Api/Customapi';
import { useQueryClient } from '@tanstack/react-query';

const { RangePicker } = DatePicker;

// ─── Shared print helper (mirrors the one in InvoiceModal) ───────────────────
const JOOD_LOGO_URL = '/headerLogo.png';

const printInvoicePDF = (currentInvoice, booking) => {
    const {
        visitPrice = 0,
        services = [],
        discountAmount = 0,
        discountPer = 0,
        paidAmount = 0,
    } = currentInvoice || {};

    const servicesTotal = services.reduce((sum, s) => sum + (s.unitPrice * s.quantity), 0);
    const subtotal = visitPrice + servicesTotal;
    const percentDiscount = (subtotal * discountPer) / 100;
    const totalDiscount = discountAmount + percentDiscount;
    const total = Math.max(0, subtotal - totalDiscount);
    const remaining = Math.max(0, total - paidAmount);

    const today = new Date().toLocaleDateString('en-GB', {
        day: '2-digit', month: 'long', year: 'numeric'
    });

    const serviceRows = services.map(s => `
        <tr>
            <td style="padding:10px 14px;border-bottom:1px solid #f0e8d0;font-size:13px;color:#3a2e1e;">${s.title || s.externalServiceTitle || 'Service'}</td>
            <td style="padding:10px 14px;border-bottom:1px solid #f0e8d0;text-align:center;font-size:13px;color:#3a2e1e;">${s.quantity}</td>
            <td style="padding:10px 14px;border-bottom:1px solid #f0e8d0;text-align:right;font-size:13px;color:#3a2e1e;">EGP ${Number(s.unitPrice).toFixed(2)}</td>
            <td style="padding:10px 14px;border-bottom:1px solid #f0e8d0;text-align:right;font-size:13px;font-weight:600;color:#8B6914;">EGP ${(s.unitPrice * s.quantity).toFixed(2)}</td>
        </tr>
    `).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>Invoice – ${booking?.patientName || 'Patient'}</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Jost:wght@300;400;500;600&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'Jost',sans-serif;background:#fff;color:#3a2e1e;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        .page{width:210mm;min-height:297mm;margin:0 auto;background:#fff;position:relative}
        .header{padding:32px 48px 24px;display:flex;align-items:flex-start;justify-content:space-between;border-bottom:2px solid #c9a84c}
        .logo-section img{height:90px;object-fit:contain}
        .invoice-meta{text-align:right}
        .invoice-meta .invoice-title{font-family:'Cormorant Garamond',serif;font-size:36px;font-weight:700;color:#c9a84c;letter-spacing:3px;text-transform:uppercase;line-height:1}
        .invoice-meta .invoice-number{font-size:12px;color:#9a8060;letter-spacing:1.5px;text-transform:uppercase;margin-top:6px}
        .gold-rule{height:3px;background:linear-gradient(90deg,#c9a84c 0%,#f0d080 50%,#c9a84c 100%)}
        .patient-bar{background:#faf6ee;padding:20px 48px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #e8dfc8}
        .patient-bar .label{font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#9a8060;margin-bottom:4px}
        .patient-bar .value{font-size:16px;font-weight:600;color:#3a2e1e;font-family:'Cormorant Garamond',serif}
        .patient-bar .booking-id{font-size:12px;color:#9a8060;margin-top:2px}
        .body{padding:32px 48px}
        .section-title{font-family:'Cormorant Garamond',serif;font-size:13px;letter-spacing:3px;text-transform:uppercase;color:#c9a84c;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid #e8dfc8}
        .visit-row{display:flex;justify-content:space-between;align-items:center;padding:12px 0;border-bottom:1px solid #f0e8d0}
        .visit-row .item-name{font-size:13px;color:#3a2e1e}
        .visit-row .item-amount{font-size:13px;font-weight:600;color:#8B6914}
        .services-table{width:100%;border-collapse:collapse;margin-top:8px}
        .services-table thead tr{background:linear-gradient(135deg,#c9a84c,#e8c96a)}
        .services-table thead th{padding:10px 14px;font-size:10px;letter-spacing:2px;text-transform:uppercase;color:#fff;font-weight:600;text-align:left}
        .services-table thead th:last-child,.services-table thead th:nth-child(3){text-align:right}
        .services-table thead th:nth-child(2){text-align:center}
        .services-table tbody tr:nth-child(even){background:#faf6ee}
        .totals-section{margin-top:28px;display:flex;justify-content:flex-end}
        .totals-box{width:280px;border:1px solid #e8dfc8;border-radius:8px;overflow:hidden}
        .totals-row{display:flex;justify-content:space-between;padding:10px 16px;font-size:13px;border-bottom:1px solid #f0e8d0}
        .totals-row:last-child{border-bottom:none}
        .totals-row .tl{color:#9a8060}
        .totals-row .tr{color:#3a2e1e;font-weight:500}
        .totals-row.discount .tl,.totals-row.discount .tr{color:#c0392b}
        .totals-row.total-final{background:linear-gradient(135deg,#c9a84c,#e8c96a);padding:14px 16px}
        .totals-row.total-final .tl,.totals-row.total-final .tr{color:#fff;font-weight:700;font-size:15px;font-family:'Cormorant Garamond',serif;letter-spacing:0.5px}
        .totals-row.paid .tr{color:#27ae60;font-weight:600}
        .totals-row.remaining .tr{color:${remaining > 0 ? '#e67e22' : '#27ae60'};font-weight:600}
        .status-badge{display:inline-block;margin-top:20px;padding:8px 20px;border-radius:24px;font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;background:${remaining === 0 ? '#e8f8f0' : '#fef3e8'};color:${remaining === 0 ? '#27ae60' : '#e67e22'};border:1px solid ${remaining === 0 ? '#a9dfbf' : '#f0c070'}}
        .footer{margin-top:40px;padding:24px 48px;border-top:1px solid #e8dfc8;display:flex;justify-content:space-between;align-items:center;background:#faf6ee}
        .footer .clinic-name{font-family:'Cormorant Garamond',serif;font-size:14px;font-weight:700;color:#c9a84c;letter-spacing:2px;text-transform:uppercase}
        .footer .clinic-sub{font-size:10px;color:#9a8060;letter-spacing:1px;text-transform:uppercase;margin-top:2px}
        .footer .thank-you{font-family:'Cormorant Garamond',serif;font-size:15px;font-style:italic;color:#9a8060}
        .ornament-tl,.ornament-br{position:fixed;width:60px;height:60px;opacity:0.15}
        .ornament-tl{top:0;left:0;border-top:3px solid #c9a84c;border-left:3px solid #c9a84c}
        .ornament-br{bottom:0;right:0;border-bottom:3px solid #c9a84c;border-right:3px solid #c9a84c}
        @media print{body{margin:0}.page{width:100%;margin:0}.no-print{display:none!important}}
    </style>
</head>
<body>
<div class="page">
    <div class="ornament-tl"></div>
    <div class="ornament-br"></div>
    <div class="header">
        <div class="logo-section">
            <img src="${JOOD_LOGO_URL}" alt="JOOD Clinic Logo" onerror="this.style.display='none'"/>
        </div>
        <div class="invoice-meta">
            <div class="invoice-title">Invoice</div>
            <div class="invoice-number">No. ${booking?.bookingCode ? '#' + booking.bookingCode : '#—'}</div>
            <div class="invoice-number" style="margin-top:4px;">${today}</div>
        </div>
    </div>
    <div class="gold-rule"></div>
    <div class="patient-bar">
        <div>
            <div class="label">Patient Name</div>
            <div class="value">${booking?.patientName || '—'}</div>
            ${booking?.bookingCode ? `<div class="booking-id">Invoice #${booking.bookingCode}</div>` : ''}
        </div>
        <div style="text-align:right;">
            <div class="label">Date</div>
            <div class="value">${booking?.createdAt ? new Date(booking.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : today}</div>
        </div>
    </div>
    <div class="body">
        ${visitPrice > 0 ? `
        <div class="section-title">Consultation</div>
        <div class="visit-row">
            <span class="item-name">Visit / Consultation Fee</span>
            <span class="item-amount">EGP ${Number(visitPrice).toFixed(2)}</span>
        </div>
        ` : ''}
        ${services.length > 0 ? `
        <div class="section-title" style="margin-top:24px;">Services Provided</div>
        <table class="services-table">
            <thead>
                <tr>
                    <th>Service</th>
                    <th style="text-align:center;">Qty</th>
                    <th style="text-align:right;">Unit Price</th>
                    <th style="text-align:right;">Total</th>
                </tr>
            </thead>
            <tbody>${serviceRows}</tbody>
        </table>
        ` : ''}
        <div class="totals-section">
            <div class="totals-box">
                <div class="totals-row">
                    <span class="tl">Subtotal</span>
                    <span class="tr">EGP ${subtotal.toFixed(2)}</span>
                </div>
                ${totalDiscount > 0 ? `
                <div class="totals-row discount">
                    <span class="tl">Discount${discountPer > 0 ? ` (${discountPer}%)` : ''}</span>
                    <span class="tr">− EGP ${totalDiscount.toFixed(2)}</span>
                </div>` : ''}
                <div class="totals-row total-final">
                    <span class="tl">Total Amount</span>
                    <span class="tr">EGP ${total.toFixed(2)}</span>
                </div>
                <div class="totals-row paid">
                    <span class="tl">Paid</span>
                    <span class="tr">EGP ${Number(paidAmount).toFixed(2)}</span>
                </div>
                <div class="totals-row remaining">
                    <span class="tl">Balance Due</span>
                    <span class="tr">EGP ${remaining.toFixed(2)}</span>
                </div>
            </div>
        </div>
        <div>
            <span class="status-badge">
                ${remaining === 0 ? '✓ Fully Paid' : `Balance Due: EGP ${remaining.toFixed(2)}`}
            </span>
        </div>
    </div>
    <div class="footer">
        <div>
            <div class="clinic-name">Jood Clinic</div>
            <div class="clinic-sub">Lymphoedema &amp; Cosmetic Clinic</div>
        </div>
        <div class="thank-you">Thank you for your trust</div>
    </div>
</div>
<script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);

    const patientSlug = (booking?.patientName || 'Patient').replace(/\s+/g, '_');
    const dateSlug = new Date().toISOString().slice(0, 10);
    const filename = `Invoice_${patientSlug}_${dateSlug}.html`;

    // 1. Download
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // 2. Open in new tab
    window.open(blobUrl, '_blank');

    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
};

// ─── InvoiceTab ───────────────────────────────────────────────────────────────
const InvoiceTab = ({ patient, onEditInvoice }) => {
    console.log(patient, "ak");

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

    // Filter states
    const [isPaidFilter, setIsPaidFilter] = useState(undefined);
    const [dateRange, setDateRange] = useState(null);
    const [sortBy, setSortBy] = useState('CreatedAt');
    const [sortDescending, setSortDescending] = useState(true);

    const [payModalVisible, setPayModalVisible] = useState(false);
    const [currentInvoice, setCurrentInvoice] = useState(null);
    const [payForm] = Form.useForm();
    const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    // Print loading state per row
    const [printingId, setPrintingId] = useState(null);

    const queryParams = {
        userId: patient?.accountId,
        pageNumber: pagination.current,
        pageSize: pagination.pageSize,
        isPaid: isPaidFilter,
        fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
        sortBy,
        sortDescending,
    };
    const [code, setCode] = useState("");
    const [validating, setValidating] = useState(false);
    const [applying, setApplying] = useState(false);
    const [validation, setValidation] = useState(null);   // null | response
    const [applied, setApplied] = useState(false);  // coupon locked in
    const [paidAmount, setPaidAmount] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const queryClient = useQueryClient();
    const { data, isLoading, refetch } = useUserAccount(queryParams);
    const { data: invoiceDetails, isLoading: isLoadingDetails } = useInvoice(
        selectedInvoiceId,
        { enabled: !!selectedInvoiceId && detailsDrawerVisible }
    );
    const { payInvoice } = useInvoiceMutations();

    const invoices = data?.items || [];
    const totalCount = data?.totalCount || 0;
    const effectiveTotal =
        applied && validation?.invoiceTotalAfter != null
            ? validation?.invoiceTotalAfter
            : currentInvoice?.totals.totalAmount;

    const remaining = Math.max(0, effectiveTotal - paidAmount);

    const isChecked = validation !== null;
    const isValid = validation?.isValid === true;
    const isInvalid = validation !== null && !validation?.isValid
    const invoiceSummary = React.useMemo(() => {
        const total = invoices.reduce((sum, inv) => sum + (inv.totals?.totalAmount || 0), 0);
        const paid = invoices.reduce((sum, inv) => sum + (inv.paidAmount || 0), 0);
        const remaining = invoices.reduce((sum, inv) => sum + (inv.remainingAmount || 0), 0);
        const unpaidCount = invoices.filter(inv => !inv.isPaid).length;
        return { total, paid, remaining, unpaidCount };
    }, [invoices]);
    const role = localStorage.getItem('role');

    // ─── Print handler ────────────────────────────────────────────────────────
    const handlePrintClick = async (record) => {
        setPrintingId(record.id);
        try {
            // Fetch full invoice details (needed for services list)
            const response = await drEasyApi.get('/api/Invoice/Get', {
                params: { InvoiceId: record.id }
            });
            const full = response.data;

            const currentInvoice = {
                visitPrice: full.visitPrice || 0,
                services: (full.services || []).map(s => ({
                    title: s.title || s.externalServiceTitle || 'Service',
                    unitPrice: s.unitPrice || 0,
                    quantity: s.quantity || 1,
                })),
                discountAmount: full.discountAmount || 0,
                discountPer: full.discountPer || 0,
                paidAmount: full.paidAmount || 0,
                bookingCode: record.id
            };

            const bookingMeta = {
                id: full.id,
                patientName: full.userName || patient?.fullName || patient?.firstName + ' ' + patient?.lastName || '—',
                createdAt: full.createdAt,
                bookingCode: record.id

            };

            printInvoicePDF(currentInvoice, bookingMeta);
        } catch (err) {
            console.error('Error fetching invoice for print:', err);
            message.error('Failed to load invoice for printing');
        } finally {
            setPrintingId(null);
        }
    };

    const handlePayClick = (invoice) => {
        setCurrentInvoice(invoice);
        const remainingAmount = invoice.remainingAmount || 0;
        payForm.setFieldsValue({ paidAmount: remainingAmount > 0 ? remainingAmount : 0 });
        setPayModalVisible(true);
    };

    const handlePaySubmit = async () => {
        try {
            const values = await payForm.validateFields();
            await payInvoice.mutateAsync({ invoiceId: currentInvoice.id, paidAmount: values.paidAmount });
            setPayModalVisible(false);
            payForm.resetFields();
            setCurrentInvoice(null);
            refetch();
        } catch (error) {
            if (error.errorFields) return;
            console.error('Error processing payment:', error);
        }
    };

    const handleEditClick = (invoice) => {
        if (onEditInvoice) onEditInvoice(invoice);
        else message.warning('Edit functionality not connected');
    };
    const handleDelete = (invoice) => {
        Modal.confirm({
            title: 'Confirm Deletion',
            content: 'Are you sure you want to delete this invoice? This action cannot be undone.',
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await drEasyApi.delete(`/api/Invoice/DeleteInvoice`, { params: { InvoiceId: invoice?.id } });
                    message.success('Invoice deleted successfully');
                    refetch();
                } catch (error) {
                    console.error('Error deleting invoice:', error);

                    message.error('Failed to delete invoice');
                }
            }
        });
    }

    const handleViewDetails = (invoice) => {
        setSelectedInvoiceId(invoice.id);
        setDetailsDrawerVisible(true);
    };

    const handleCloseDetails = () => {
        setDetailsDrawerVisible(false);
        setSelectedInvoiceId(null);
    };

    const handleClearFilters = () => {
        setIsPaidFilter(undefined);
        setDateRange(null);
        setSortBy('CreatedAt');
        setSortDescending(true);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const hasActiveFilters = isPaidFilter !== undefined || dateRange !== null || sortBy !== 'CreatedAt' || !sortDescending;
    const handleValidate = async () => {
        const trimmed = code?.trim().toUpperCase();
        if (!trimmed) { message.warning("Please enter a coupon code"); return; }
        setValidating(true);
        setValidation(null);
        setApplied(false);
        try {
            const res = await drEasyApi.get("/api/Coupons/Validate", {
                params: { CouponCode: trimmed, InvoiceId: currentInvoice.id, },
            });
            setValidation(res.data);
            if (!res.data.isValid) message.error(res.data.message || "Invalid coupon");
        } catch {
            message.error("Failed to validate coupon");
        } finally {
            setValidating(false);
        }
    };

    // ── apply ─────────────────────────────────────────────────────────────────
    const handleApply = async () => {
        if (!isValid) return;
        setApplying(true);
        try {
            await drEasyApi.post("/api/Coupons/Apply", {
                invoiceId: currentInvoice.id,
                couponCode: validation?.code,
            });
            setApplied(true);
            queryClient.invalidateQueries({ queryKey: ["userAccount"] });
            queryClient.invalidateQueries({ queryKey: ["bookings"] });
            message.success(`Coupon applied — saving EGP ${Number(validation?.estimatedDiscount).toFixed(2)}!`);
        } catch (err) {
            message.error(err.response?.data?.message || "Failed to apply coupon");
        } finally {
            setApplying(false);
        }
    };

    // ─── Table columns ────────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Invoice ID',
            dataIndex: 'id',
            key: 'id',
            width: 120,
            render: (id) => (
                <Tooltip title={id}>
                    <span className="text-xs font-mono">#{id.slice(0, 8)}...</span>
                </Tooltip>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            render: (date) => dayjs(date).format('MMM D, YYYY'),
        },
        {
            title: 'Visit Price',
            dataIndex: 'visitPrice',
            key: 'visitPrice',
            width: 100,
            render: (price) => (
                <span className="font-semibold text-blue-600">EGP {price?.toFixed(2) || '0.00'}</span>
            ),
        },
        {
            title: 'Total After Discount    ',
            key: 'totals',
            width: 140,
            render: (_, record) => (        // ← use render with record instead of dataIndex
                <span className="font-bold text-green-600">
                    EGP {Number(record.totals?.totalAmount ?? 0).toFixed(2)}
                </span>
            ),
        },
        {
            title: 'Paid',
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            width: 100,
            render: (paidAmount) => (
                <span className="font-semibold flex items-center text-emerald-600">EGP {paidAmount?.toFixed(2) || '0.00'}</span>
            ),
        },
        {
            title: 'Discount',
            dataIndex: 'couponDiscountAmount',
            key: 'couponDiscountAmount',
            width: 100,
            render: (_, record) => {
                const couponDiscountAmount = record.couponDiscountAmount || 0;
                const coupon = record.applliedCoupoun || null;
                return (
                    <span className="text-gray-400 "> {coupon}<span className='font-semibold text-amber-600'>  EGP {couponDiscountAmount?.toFixed(2) || '0.00'}</span></span>
                );
            },
        },
        {
            title: 'Remaining',
            key: 'remaining',
            width: 100,
            render: (_, record) => {
                const remaining = record.remainingAmount || 0;
                return (
                    <span className={`font-semibold ${remaining > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                        EGP {(remaining >= 0 ? remaining : 0).toFixed(2)}
                    </span>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'remainingAmount',
            key: 'remainingAmount',
            width: 100,
            render: (remainingAmount) => (
                <Tag
                    color={remainingAmount <= 0 ? 'green' : 'orange'}
                    icon={remainingAmount <= 0 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                >
                    {remainingAmount <= 0 ? 'Paid' : 'Unpaid'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 170,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="View Details">
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDetails(record)}
                            size="small"
                            className="text-blue-500 hover:text-blue-700"
                        />
                    </Tooltip>

                    {/* ── Print Button ── */}
                    <Tooltip title="Print Invoice">
                        <Button
                            type="text"
                            icon={<PrinterOutlined />}
                            onClick={() => handlePrintClick(record)}
                            size="small"
                            loading={printingId === record.id}
                            style={{ color: '#c9a84c' }}
                            className="hover:text-yellow-600"
                        />
                    </Tooltip>
                    {role == 4 &&

                        !record.isPaid && (
                            <Tooltip title="Pay Invoice">
                                <Button
                                    type="text"
                                    icon={<DollarOutlined />}
                                    onClick={() => handlePayClick(record)}
                                    size="small"
                                    className="text-green-500 hover:text-green-700"
                                />
                            </Tooltip>
                        )}
                    {role == 4 &&

                        <Tooltip title="Edit Invoice">
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => handleEditClick(record)}
                                size="small"
                                className="text-red-600 hover:text-orange-700"
                            />
                        </Tooltip>}
                    {role == 3 &&
                        <Tooltip title="Delete Invoice">
                            <Button
                                type="text"
                                icon={<FaTrash />}
                                onClick={() => handleDelete(record)}
                                size="small"
                                className="text-red-600 hover:text-orange-700"
                            />
                        </Tooltip>}
                </Space>
            ),
        },
    ];

    return (

        <div className="space-y-4">
            <style>{`
            .white-header-table .ant-table-thead > tr > th {
                color: white !important;
            }
        `}</style>
            {/* Summary Statistics */}
            <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                    <Card className="shadow-sm">
                        <Statistic
                            title="Total Amount"
                            value={(invoiceSummary.total).toFixed(2)}
                            precision={2}
                            prefix="EGP"
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card className="shadow-sm">
                        <Statistic
                            title="Paid Amount"
                            value={invoiceSummary.paid}
                            precision={2}
                            prefix="EGP"
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={8}>
                    <Card className="shadow-sm">
                        <Statistic
                            title="Remaining Balance"
                            value={invoiceSummary.remaining >= 0 ? invoiceSummary.remaining : 0}
                            precision={2}
                            prefix="EGP"
                            valueStyle={{ color: invoiceSummary.remaining > 0 ? '#cf1322' : '#666' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filter Controls */}
            <div className="space-y-3">
                <div className="flex gap-2 flex-wrap items-center">
                    <Button size="small" type={isPaidFilter === undefined ? 'primary' : 'default'} onClick={() => { setIsPaidFilter(undefined); setPagination(prev => ({ ...prev, current: 1 })); }}>All</Button>
                    <Button size="small" type={isPaidFilter === true ? 'primary' : 'default'} onClick={() => { setIsPaidFilter(true); setPagination(prev => ({ ...prev, current: 1 })); }} className={isPaidFilter === true ? 'bg-green-500 border-green-500' : ''}>Paid</Button>
                    <Button size="small" type={isPaidFilter === false ? 'primary' : 'default'} onClick={() => { setIsPaidFilter(false); setPagination(prev => ({ ...prev, current: 1 })); }} className={isPaidFilter === false ? 'bg-red-600 border-red-600' : ''}>Unpaid</Button>
                    <div className="border-l border-gray-300 h-6 mx-2" />
                    <Button size="small" icon={<FilterOutlined />} onClick={() => setShowFilters(!showFilters)} type={showFilters ? 'primary' : 'default'}>{showFilters ? 'Hide' : 'Show'} Filters</Button>
                    {hasActiveFilters && (
                        <Button size="small" icon={<ClearOutlined />} onClick={handleClearFilters} />
                    )}
                </div>

                {showFilters && (
                    <Card className="shadow-sm" size="small">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                                <RangePicker value={dateRange} onChange={(dates) => { setDateRange(dates); setPagination(prev => ({ ...prev, current: 1 })); }} className="w-full" format="YYYY-MM-DD" placeholder={['From Date', 'To Date']} allowClear />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                                <Select value={sortBy} onChange={(value) => { setSortBy(value); setPagination(prev => ({ ...prev, current: 1 })); }} className="w-full" allowClear onClear={() => { setSortBy('CreatedAt'); setPagination(prev => ({ ...prev, current: 1 })); }} options={[{ label: 'Created Date', value: 'CreatedAt' }, { label: 'Total Amount', value: 'TotalAmount' }, { label: 'Paid Amount', value: 'PaidAmount' }, { label: 'Remaining Amount', value: 'RemainingAmount' }]} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                                <Select value={sortDescending ? 'desc' : 'asc'} onChange={(value) => { setSortDescending(value === 'desc'); setPagination(prev => ({ ...prev, current: 1 })); }} className="w-full" allowClear onClear={() => { setSortDescending(true); setPagination(prev => ({ ...prev, current: 1 })); }} options={[{ label: 'Descending (Newest First)', value: 'desc' }, { label: 'Ascending (Oldest First)', value: 'asc' }]} />
                            </div>
                        </div>
                    </Card>
                )}
            </div>

            {/* Invoices Table */}
            <Spin spinning={isLoading}>
                <Table
                    className="white-header-table"

                    columns={columns}
                    dataSource={invoices}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: totalCount,
                        onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} invoices`,
                    }}
                    scroll={{ x: 900 }}
                    size="small"
                    locale={{
                        emptyText: (
                            <div className="text-center py-8 text-gray-500">
                                <FaFileInvoiceDollar className="text-4xl mx-auto mb-2 opacity-50" />
                                <div>No invoices found</div>
                            </div>
                        )
                    }}
                />
            </Spin>

            {/* Pay Invoice Drawer */}
            <Drawer
                title={
                    <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center mr-3">
                            <FaMoneyBillWave className="text-white text-sm" />
                        </div>
                        <span>Pay Invoice</span>
                    </div>
                }
                placement="right"
                onClose={() => { setPayModalVisible(false); payForm.resetFields(); setCurrentInvoice(null); }}
                open={payModalVisible}
                width={500}
                footer={
                    <div className="flex gap-2 justify-end">
                        <Button onClick={() => { setPayModalVisible(false); payForm.resetFields(); setCurrentInvoice(null); }}>Cancel</Button>
                        <Button type="primary" onClick={handlePaySubmit} loading={payInvoice.isPending} icon={<CheckOutlined />} className="bg-green-500 border-green-500">Process Payment</Button>
                    </div>
                }
            >
                {currentInvoice && (
                    <div>
                        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Total Amount:</span>
                                    <span className="font-bold text-blue-600">EGP {(currentInvoice.totals?.totalAmount || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Already Paid:</span>
                                    <span className="font-semibold text-green-600">EGP {(currentInvoice.paidAmount || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-blue-300">
                                    <span className="text-gray-700 font-medium">Remaining:</span>
                                    <span className="font-bold text-red-600">EGP {(currentInvoice.remainingAmount || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                        {/* ══ COUPON SECTION ═══════════════════════════════════════════════════ */}
                        <Divider className="!my-3" />
                        {!currentInvoice?.applliedCoupoun &&
                            <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                                <GiftOutlined className="text-primary" />
                                Coupon 
                            </p>
                        }
                        {/* ── Input + two-button row ── */}
                        {currentInvoice?.applliedCoupoun ? (
                            <div className="flex gap-2 mb-2">


                                {/* Validate button — shown until code is confirmed valid */}


                                <span className="bg-green-200  p-1 px-2 rounded border border-green-500 text-green-500">Applied Coupon: {currentInvoice?.applliedCoupoun}</span>
                            </div>
                        ) : !applied ? (
                            <div className="flex gap-2 mb-2">
                                <Input
                                    value={code ? code : ""}
                                    onChange={(e) => {
                                        setCode(e.target.value.toUpperCase());
                                        // reset validation when user edits
                                        if (validation) { setValidation(null); setApplied(false); }
                                    }}
                                    onKeyDown={(e) => e.key === "Enter" && !isValid && handleValidate()}
                                    placeholder="Enter promo code"
                                    prefix={<TagOutlined className="text-gray-400" />}
                                    style={{ fontFamily: "monospace", letterSpacing: "0.06em" }}
                                    className={
                                        isValid ? "!border-green-400 focus:!border-green-500" :
                                            isInvalid ? "!border-red-400   focus:!border-red-500" : ""
                                    }
                                    disabled={validating || applying || submitting}
                                    suffix={
                                        code ? (
                                            <CloseOutlined
                                                className="text-gray-400 cursor-pointer hover:text-gray-600 text-xs"
                                            //   onClick={clearCoupon}
                                            />
                                        ) : null
                                    }
                                />

                                {/* Validate button — shown until code is confirmed valid */}
                                {!isValid && (
                                    <Button
                                        onClick={handleValidate}
                                        loading={validating}
                                        disabled={!code?.trim() || applying || submitting}
                                        className="shrink-0"
                                        style={{ backgroundColor: "#298d9f", borderColor: "#298d9f", color: "#fff" }}
                                    >
                                        Apply
                                    </Button>
                                )}

                                {/* Apply button — only shown after successful validation */}
                                {isValid && !applied && (
                                    <Button
                                        onClick={handleApply}
                                        loading={applying}
                                        disabled={submitting}
                                        className="shrink-0"
                                        style={{ backgroundColor: "#16a34a", borderColor: "#16a34a", color: "#fff" }}
                                    >
                                        Use Coupon
                                    </Button>
                                )}
                            </div>
                        ) : (<div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2">
                            <div className="flex items-center gap-2">
                                <CheckCircleFilled className="text-green-500" />
                                <span className="font-mono font-semibold text-green-700 tracking-wider">
                                    {validation?.code}
                                </span>
                                <span className="text-green-600 text-sm">
                                    — saving EGP {Number(validation?.estimatedDiscount).toFixed(2)}
                                </span>
                            </div>

                        </div >
                        )}

                        {/* ── Inline feedback (invalid only; valid transitions to Apply btn) ── */}
                        {
                            isInvalid && (
                                <p className="text-red-500 text-xs flex items-center gap-1 mb-1">
                                    <CloseCircleFilled />
                                    {validation?.message || "Invalid coupon code"}
                                </p>
                            )
                        }

                        {/* ── Preview row (valid but not yet applied) ── */}
                        {
                            isValid && !applied && (
                                <p className="text-green-600 text-xs flex items-center gap-1 mb-1">
                                    <CheckCircleFilled />
                                    {validation?.type === 1
                                        ? `${validation?.value}% off`
                                        : `EGP ${validation?.value} off`}
                                    {" "}— total becomes{" "}
                                    <strong>EGP {Number(validation?.invoiceTotalAfter).toFixed(2)}</strong>
                                </p>
                            )
                        }

                        {/* ── Updated total after coupon applied ── */}
                        {
                            applied && (
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm text-gray-500">Total after coupon</span>
                                    <span className="font-bold text-primary text-base">
                                        EGP {Number(effectiveTotal).toFixed(2)}
                                    </span>
                                </div>
                            )
                        }
                        <Form form={payForm} layout="vertical">
                            <Form.Item name="paidAmount" label="Payment Amount" rules={[{ required: true, message: 'Please enter payment amount' }, { type: 'number', min: 0.01, message: 'Amount must be greater than 0' }]}>
                                <InputNumber placeholder="Enter payment amount" className="w-full rounded-lg" size="large" prefix="EGP" min={0} step={0.01} precision={2} />
                            </Form.Item>
                        </Form>
                    </div>
                )}

            </Drawer>

            {/* Invoice Details Drawer */}
            <Drawer
                title={
                    <div className="flex items-center">
                        <FileTextOutlined className="mr-2 text-blue-500" />
                        Invoice #{invoiceDetails?.id.slice(0, 8)}
                    </div>
                }
                placement="right"
                onClose={handleCloseDetails}
                open={detailsDrawerVisible}
                width={500}
            >
                <Spin spinning={isLoadingDetails}>
                    {invoiceDetails && (
                        <div className="space-y-4">
                            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                                <div className="text-sm text-gray-600 space-y-1">
                                    <div className='flex items-center gap-2'><CalendarOutlined /> Created At: {dayjs(invoiceDetails.createdAt).format('MMMM D, YYYY h:mm A')}</div>
                                    <div className='font-semibold flex items-center gap-2'><UserOutlined /> Created by: {invoiceDetails.createdByName}</div>
                                    {invoiceDetails.userName && <div>Patient: {invoiceDetails.userName}</div>}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                    <span className="text-gray-600">Visit Price</span>
                                    <span className="font-semibold text-blue-600">EGP {invoiceDetails.visitPrice?.toFixed(2) || '0.00'}</span>
                                </div>
                                {invoiceDetails.discountAmount > 0 && (
                                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                                        <span className="text-gray-600">Discount {invoiceDetails.discountPer > 0 ? `(${invoiceDetails.discountPer}%)` : ''}</span>
                                        <span className="font-semibold text-red-600">-EGP {invoiceDetails.discountAmount?.toFixed(2) || '0.00'}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                                    <span className="text-gray-700 font-medium">Total Amount</span>
                                    <span className="font-bold text-green-600 text-lg">EGP {(invoiceDetails.totals?.totalAmount || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                                    <span className="text-gray-600">Paid Amount</span>
                                    <span className="font-semibold text-emerald-600">EGP {invoiceDetails.paidAmount?.toFixed(2) || '0.00'}</span>
                                </div>
                                <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border-2 border-orange-200">
                                    <span className="text-gray-700 font-medium">Remaining Balance</span>
                                    <span className="font-bold text-red-600 text-lg">EGP {(invoiceDetails.remainingAmount >= 0 ? invoiceDetails.remainingAmount : 0).toFixed(2)}</span>
                                </div>
                            </div>

                            {invoiceDetails.services && invoiceDetails.services.length > 0 && (
                                <div className="mt-6">
                                    <h4 className="font-semibold mb-3 text-gray-800">Services Included</h4>
                                    <div className="space-y-2">
                                        {invoiceDetails.services.map((service) => (
                                            <div key={service.invoiceServiceId} className="p-3 bg-white rounded-lg border border-gray-200">
                                                <div className="flex items-start gap-3">
                                                    {service.photoID && (
                                                        <img src={getImg(service.photoID)} className="w-12 h-12 rounded-lg object-cover" alt={service.title} />
                                                    )}
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-800">{service.title}</div>
                                                        <div className="text-sm text-gray-600 mt-1">Quantity: {service.quantity} × EGP{service.unitPrice?.toFixed(2)}</div>
                                                        <div className="text-sm font-semibold text-green-600 mt-1">Total: EGP {service.chargedAmount?.toFixed(2)}</div>
                                                        {service.isFromPackage && <Tag color="blue" className="mt-1" size="small">From Package</Tag>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {invoiceDetails.totals && (
                                        <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                                            <div className="flex justify-between">
                                                <span>Total Services:</span>
                                                <span className="font-semibold">{invoiceDetails.totals.totalServicesCount}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Services Charged Amount:</span>
                                                <span className="font-semibold text-green-600">EGP {invoiceDetails.totals.totalServicesChargedAmount?.toFixed(2)}</span>
                                            </div>
                                            {invoiceDetails.totals.totalPackageCoveredAmount > 0 && (
                                                <div className="flex justify-between">
                                                    <span>Package Covered:</span>
                                                    <span className="font-semibold text-blue-600">EGP {invoiceDetails.totals.totalPackageCoveredAmount?.toFixed(2)}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="pt-4 flex gap-2">
                                {!invoiceDetails.isPaid && (
                                    <Button type="primary" icon={<DollarOutlined />} onClick={() => { handleCloseDetails(); handlePayClick(invoiceDetails); }} className="flex-1 bg-green-500 border-green-500">Pay Invoice</Button>
                                )}
                                <Button icon={<EditOutlined />} onClick={() => { handleCloseDetails(); handleEditClick(invoiceDetails); }} className="flex-1">Edit Invoice</Button>
                                <Tooltip title="Print Invoice">
                                    <Button
                                        icon={<PrinterOutlined />}
                                        onClick={() => handlePrintClick(invoiceDetails)}
                                        loading={printingId === invoiceDetails.id}
                                        style={{ color: '#c9a84c', borderColor: '#c9a84c' }}
                                    >
                                        Print
                                    </Button>
                                </Tooltip>
                            </div>
                        </div>
                    )}
                </Spin>
            </Drawer>
        </div>
    );
};

export default InvoiceTab;